import { api } from "@/api/axios.ts";
import { ApiIdObject, ApiOperationResult } from "@/types/common.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import {
  ApiWorkflowBlockActionCandidateEmailSave,
  ApiWorkflowBlockActionCandidateStageChangeSave,
  ApiWorkflowBlockActionGoogleWorkspaceSave,
  ApiWorkflowBlockActionHTTPRequestSave,
  ApiWorkflowBlockActionHTTPRequestSignatureSecret,
  ApiWorkflowBlockActionInvitationSave,
  ApiWorkflowBlockActionNotificationSave,
  ApiWorkflowBlockActionApprovalSave,
  ApiWorkflowBlockActionEsignSave,
  ApiWorkflowBlockActionPerformanceReviewEnrollSave,
  ApiWorkflowBlockActionPerformanceReviewSave,
  ApiWorkflowBlockActionRunWorkflowSave,
  ApiWorkflowBlockActionSavePayload,
  ApiWorkflowBlockActionSurveyEnrollSave,
  ApiWorkflowBlockActionSurveySave,
  ApiWorkflowBlockActionTaskSave,
  ApiWorkflowBlockActionMeetingSave,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiCalendar } from "@/types/calendar.types.ts";
import { ApiWorkflowConditionSave } from "@/types/workflows/workflow.condition.types.ts";
import {
  ApiWorkflow,
  ApiWorkflowBlock,
  ApiWorkflowBlockMove,
  ApiWorkflowBlockSave,
  ApiWorkflowCreate,
  ApiWorkflowFilter,
  ApiWorkflowListItem,
  ApiWorkflowShort,
  ApiWorkflowSummary,
  ApiWorkflowTriggerScheduleRuleSave,
  ApiWorkflowTriggerRecurringRule,
  ApiWorkflowUpdateDetails,
  ApiWorkflowUpdateTrigger,
  ApiWorkflowVersion,
} from "@/types/workflows/workflow.types.ts";

export const apiWorkflows = {
  getById: (workflowId: number, signal?: AbortSignal) => {
    return api.get<ApiWorkflow>(`/api/v1/workflows/${workflowId}`, {}, signal);
  },
  getShared: () => {
    return api.get<ApiWorkflowShort[]>(`/api/v1/workflows/shared`);
  },
  getSummary: (workflowId: number, signal?: AbortSignal) => {
    return api.get<ApiWorkflowSummary>(`/api/v1/workflows/${workflowId}/summary`, {}, signal);
  },
  getVersionById: (workflowId: number, versionId: number, signal?: AbortSignal) => {
    return api.get<ApiWorkflowVersion>(
      `/api/v1/workflows/${workflowId}/versions/${versionId}`,
      {},
      signal
    );
  },
  search: async function (filter: ApiWorkflowFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiWorkflowListItem>>("/api/v1/workflows/search", filter, signal);
  },
  updateDetails: async function (
    workflowId: number,
    workflowVersionId: number,
    payload: ApiWorkflowUpdateDetails
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/details`,
      payload
    );
  },
  updateTrigger: async function (
    workflowId: number,
    workflowVersionId: number,
    payload: ApiWorkflowUpdateTrigger
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/trigger`,
      payload
    );
  },
  updateTriggerScheduleRule: async function (
    workflowId: number,
    workflowVersionId: number,
    payload: ApiWorkflowTriggerScheduleRuleSave
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/trigger-schedule-rule`,
      payload
    );
  },
  updateTriggerRecurringRule: async function (
    workflowId: number,
    workflowVersionId: number,
    payload: ApiWorkflowTriggerRecurringRule
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/trigger-recurring-rule`,
      payload
    );
  },
  updateTriggerConditions: async function (
    id: number,
    workflowVersionId: number,
    payload: ApiWorkflowConditionSave[]
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${id}/version/${workflowVersionId}/trigger-conditions`,
      payload
    );
  },
  activate: async function (workflowId: number, workflowVersionId: number) {
    return api.post<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/activate`,
      {}
    );
  },
  deactivate: async function (workflowId: number, workflowVersionId: number) {
    return api.post<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/deactivate`,
      {}
    );
  },
  create: async function (payload: ApiWorkflowCreate) {
    return api.post<ApiIdObject>(`/api/v1/workflows/create`, payload);
  },
  delete: async function (workflowId: number) {
    return api.delete<ApiOperationResult>(`/api/v1/workflows/${workflowId}`);
  },
  duplicate: async function (workflowId: number) {
    return api.post<ApiIdObject>(`/api/v1/workflows/${workflowId}/duplicate`, {});
  },

  createBlock: async function (
    workflowId: number,
    workflowVersionId: number,
    payload: ApiWorkflowBlockSave
  ) {
    return api.post<ApiWorkflowBlock>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks`,
      payload
    );
  },

  updateBlockActionSendNotification: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionNotificationSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/send-notification`,
      payload
    );
  },

  updateBlockActionCandidateEmail: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionCandidateEmailSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/candidate-email`,
      payload
    );
  },

  updateBlockActionCandidateStageChange: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionCandidateStageChangeSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/candidate-stage-change`,
      payload
    );
  },

  updateBlockActionSendSurvey: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionSurveySave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/survey`,
      payload
    );
  },

  updateBlockActionPerformanceReview: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionPerformanceReviewSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/performance-review`,
      payload
    );
  },

  updateBlockActionPerformanceReviewEnroll: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionPerformanceReviewEnrollSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/performance-review-enroll`,
      payload
    );
  },

  updateBlockActionSurveyEnroll: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionSurveyEnrollSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/survey-enroll`,
      payload
    );
  },

  updateBlockActionHTTPRequest: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionHTTPRequestSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/http-request`,
      payload
    );
  },

  rotateBlockActionHTTPRequestSignatureSecret: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number
  ) {
    return api.post<ApiWorkflowBlockActionHTTPRequestSignatureSecret>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/http-request/rotate-signature-secret`,
      {}
    );
  },

  updateBlockActionGoogleWorkspace: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionGoogleWorkspaceSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/google-workspace`,
      payload
    );
  },

  updateBlockActionInvitation: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionInvitationSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/invitation`,
      payload
    );
  },

  updateBlockActionTask: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionTaskSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/task`,
      payload
    );
  },

  updateBlockActionRunWorkflow: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionRunWorkflowSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/run-workflow`,
      payload
    );
  },

  updateBlockActionApproval: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionApprovalSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/survey-with-approval`,
      payload
    );
  },

  updateBlockActionEsign: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionEsignSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/esign`,
      payload
    );
  },

  updateBlockActionMeeting: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionMeetingSave>
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/actions/meeting`,
      payload
    );
  },

  getMeetingOrganizerCalendars: (workflowId: number, organizerId: number, signal?: AbortSignal) => {
    return api.get<ApiCalendar[]>(
      `/api/v1/workflows/${workflowId}/meeting-organizers/${organizerId}/calendars`,
      {},
      signal
    );
  },

  deleteBlock: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number
  ) {
    return api.delete<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}`
    );
  },

  moveBlock: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number,
    payload: ApiWorkflowBlockMove
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/move`,
      payload
    );
  },

  duplicateBlock: async function (
    workflowId: number,
    workflowVersionId: number,
    workflowBlockId: number
  ) {
    return api.post<ApiIdObject>(
      `/api/v1/workflows/setup/${workflowId}/version/${workflowVersionId}/blocks/${workflowBlockId}/duplicate`,
      {}
    );
  },
};
